import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GridModule, GridDataResult } from '@progress/kendo-angular-grid';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import { BomNode, BomQueryOptions, PartType } from '../../models/file-vault.models';
import { LifecycleBadgeComponent } from '../lifecycle-badge/lifecycle-badge.component';

export type WhereUsedViewMode = 'list' | 'tree';

export interface WhereUsedSelectionEvent {
  node: BomNode;
  partId: string;
  partNumber: string;
}

interface FlattenedWhereUsedItem {
  id: string;
  level: number;
  partId: string;
  partNumber: string;
  partName: string;
  revision?: string;
  partType?: PartType;
  lifecycleState?: number;
  quantity: number;
  findNumber?: string;
  unitOfMeasure?: string;
  node: BomNode;
}

@Component({
  selector: 'fv-where-used',
  standalone: true,
  template: `
    <div class="where-used-container">
      <!-- Toolbar -->
      <div class="toolbar" *ngIf="showToolbar">
        <div class="toolbar-left">
          <span class="title" *ngIf="partNumber">Where Used: {{ partNumber }}</span>
          <span class="count" *ngIf="!isLoading">({{ totalCount }} parent(s))</span>
        </div>
        <div class="toolbar-right">
          <div class="view-toggle">
            <button class="toggle-btn" [class.active]="viewMode === 'list'" (click)="viewMode = 'list'">
              List
            </button>
            <button class="toggle-btn" [class.active]="viewMode === 'tree'" (click)="viewMode = 'tree'">
              Indented
            </button>
          </div>
          <button kendoButton [look]="'flat'" (click)="refresh()">
            Refresh
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div class="loading" *ngIf="isLoading">
        <kendo-loader [type]="'infinite-spinner'" [size]="'medium'"></kendo-loader>
        <span>Loading where-used...</span>
      </div>

      <!-- Empty State -->
      <div class="empty" *ngIf="!isLoading && flattenedData.length === 0">
        <div class="empty-icon">🔍</div>
        <div class="empty-text">Not used in any assembly</div>
        <div class="empty-hint">This part is not a component of any other part</div>
      </div>

      <!-- Where-Used Grid -->
      <kendo-grid
        *ngIf="!isLoading && flattenedData.length > 0"
        [data]="gridData"
        [selectable]="{ mode: 'single' }"
        [sortable]="true"
        [pageable]="flattenedData.length > 25"
        [pageSize]="25"
        (selectionChange)="onSelectionChange($event)"
        class="where-used-grid">

        <kendo-grid-column field="level" title="Lvl" [width]="50" *ngIf="viewMode === 'tree'">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.level }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="partNumber" title="Parent Part Number" [width]="180">
          <ng-template kendoGridCellTemplate let-dataItem>
            <div class="part-cell" [style.paddingLeft.px]="viewMode === 'tree' ? dataItem.level * 16 : 0">
              <span class="part-icon">{{ getPartIcon(dataItem) }}</span>
              <span class="part-number">{{ dataItem.partNumber }}</span>
            </div>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="partName" title="Name" [width]="250">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.partName }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="revision" title="Rev" [width]="60">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.revision || '-' }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="quantity" title="Qty Used" [width]="100">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.quantity }}
            <span class="uom" *ngIf="dataItem.unitOfMeasure">
              {{ dataItem.unitOfMeasure }}
            </span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="findNumber" title="Find #" [width]="80">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.findNumber || '-' }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="lifecycleState" title="State" [width]="120">
          <ng-template kendoGridCellTemplate let-dataItem>
            <fv-lifecycle-badge
              [state]="dataItem.lifecycleState"
              [showIcon]="false">
            </fv-lifecycle-badge>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="partType" title="Type" [width]="100">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ getPartTypeLabel(dataItem.partType) }}
          </ng-template>
        </kendo-grid-column>
      </kendo-grid>
    </div>
  `,
  styles: [`
    .where-used-container {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 12px;
      background: var(--fv-surface-hover);
      border-bottom: 1px solid var(--fv-border);
    }

    .toolbar-left {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .toolbar-right {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .title {
      font-weight: 600;
      font-size: 14px;
    }

    .count {
      font-size: 12px;
      color: var(--fv-text-secondary);
    }

    .view-toggle {
      display: flex;
      gap: 2px;
    }

    .toggle-btn {
      padding: 4px 10px;
      border: 1px solid var(--fv-border);
      background: var(--fv-surface);
      cursor: pointer;
      font-size: 12px;
      transition: all 0.2s;
    }

    .toggle-btn:first-child {
      border-radius: 4px 0 0 4px;
    }

    .toggle-btn:last-child {
      border-radius: 0 4px 4px 0;
    }

    .toggle-btn:hover {
      background: var(--fv-surface-hover);
    }

    .toggle-btn.active {
      background: var(--fv-text-accent);
      color: white;
      border-color: var(--fv-text-accent);
    }

    .loading {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 48px;
      gap: 12px;
      color: var(--fv-text-secondary);
    }

    .empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 48px;
      color: var(--fv-text-secondary);
    }

    .empty-icon {
      font-size: 48px;
      margin-bottom: 12px;
    }

    .empty-text {
      font-size: 16px;
      font-weight: 500;
    }

    .empty-hint {
      font-size: 13px;
      color: var(--fv-text-muted);
      margin-top: 4px;
    }

    .where-used-grid {
      flex: 1;
      font-size: 13px;
    }

    .part-cell {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .part-icon {
      font-size: 14px;
    }

    .part-number {
      font-family: monospace;
      font-weight: 500;
    }

    .uom {
      font-size: 11px;
      color: var(--fv-text-secondary);
      margin-left: 2px;
    }
  `],
  imports: [CommonModule, GridModule, ButtonsModule, IndicatorsModule, LifecycleBadgeComponent]
})
export class WhereUsedComponent implements OnInit, OnChanges {
  private api = inject(FileVaultApiService);

  /** Part ID to find where-used for */
  @Input() partId?: string;

  /** Part number for display (and lookup if partId not provided) */
  @Input() partNumber?: string;

  /** View mode */
  @Input() viewMode: WhereUsedViewMode = 'list';

  /** Maximum depth for tree view */
  @Input() maxDepth = 5;

  /** Show toolbar */
  @Input() showToolbar = true;

  /** Include inactive relationships */
  @Input() includeInactive = false;

  /** Emitted when a parent is selected */
  @Output() parentSelected = new EventEmitter<WhereUsedSelectionEvent>();

  whereUsedData: BomNode[] = [];
  flattenedData: FlattenedWhereUsedItem[] = [];
  isLoading = false;
  private resolvedPartId?: string;

  private nodeIdCounter = 0;

  get totalCount(): number {
    return this.whereUsedData.length;
  }

  get gridData(): GridDataResult {
    return {
      data: this.flattenedData,
      total: this.flattenedData.length
    };
  }

  ngOnInit(): void {
    this.loadWhereUsed();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['partId'] || changes['partNumber']) && !changes['partId']?.firstChange) {
      this.loadWhereUsed();
    }
  }

  async loadWhereUsed(): Promise<void> {
    if (!this.partId && !this.partNumber) {
      this.whereUsedData = [];
      this.flattenedData = [];
      return;
    }

    this.isLoading = true;
    this.nodeIdCounter = 0;

    try {
      const options: BomQueryOptions = {
        maxDepth: this.maxDepth,
        includeInactive: this.includeInactive
      };

      const currentPartId = await this.getCurrentPartId();
      if (!currentPartId) {
        this.whereUsedData = [];
        this.flattenedData = [];
        return;
      }
      const nodes = await this.api.getWhereUsed(currentPartId, options).toPromise() ?? [];
      this.whereUsedData = nodes;
      this.flattenedData = this.flattenNodes(nodes, 0);
    } catch (error) {
      console.error('Failed to load where-used', error);
      this.whereUsedData = [];
      this.flattenedData = [];
    } finally {
      this.isLoading = false;
    }
  }

  onSelectionChange(event: any): void {
    if (event.selectedRows?.length > 0) {
      const item = event.selectedRows[0].dataItem as FlattenedWhereUsedItem;
      this.parentSelected.emit({
        node: item.node,
        partId: item.partId,
        partNumber: item.partNumber
      });
    }
  }

  refresh(): void {
    this.loadWhereUsed();
  }

  getPartIcon(item: FlattenedWhereUsedItem): string {
    if (item.partType === undefined) return '📦';

    const iconMap: Record<number, string> = {
      [PartType.Assembly]: '📦',
      [PartType.Component]: '🔧',
      [PartType.RawMaterial]: '🪨',
      [PartType.Purchased]: '🛒',
      [PartType.Fabricated]: '⚙️',
      [PartType.Consumable]: '🧴',
      [PartType.Tool]: '🔨',
      [PartType.Phantom]: '👻'
    };

    return iconMap[item.partType] ?? '📦';
  }

  getPartTypeLabel(partType?: PartType): string {
    if (partType === undefined) return '-';

    const labels: Record<number, string> = {
      [PartType.Assembly]: 'Assembly',
      [PartType.Component]: 'Component',
      [PartType.RawMaterial]: 'Raw Material',
      [PartType.Purchased]: 'Purchased',
      [PartType.Fabricated]: 'Fabricated',
      [PartType.Consumable]: 'Consumable',
      [PartType.Tool]: 'Tool',
      [PartType.Phantom]: 'Phantom'
    };

    return labels[partType] ?? '-';
  }

  private flattenNodes(nodes: BomNode[], level: number): FlattenedWhereUsedItem[] {
    const result: FlattenedWhereUsedItem[] = [];

    for (const node of nodes) {
      result.push({
        id: `node-${++this.nodeIdCounter}`,
        level,
        partId: node.part?.id || '',
        partNumber: node.part?.partNumber || '',
        partName: node.part?.name || '',
        revision: node.version?.revision || node.part?.currentRevision,
        partType: node.part?.partType,
        lifecycleState: node.part?.lifecycleState,
        quantity: node.quantity,
        findNumber: node.findNumber,
        unitOfMeasure: node.relationship?.unitOfMeasure,
        node
      });

      if (node.children?.length) {
        result.push(...this.flattenNodes(node.children, level + 1));
      }
    }

    return result;
  }

  private async getCurrentPartId(): Promise<string | undefined> {
    if (this.partId) {
      this.resolvedPartId = this.partId;
      return this.partId;
    }

    if (this.resolvedPartId && this.partNumber === undefined) {
      return this.resolvedPartId;
    }

    if (!this.partNumber) return undefined;

    try {
      const part = await this.api.getPartByNumber(this.partNumber).toPromise();
      if (part?.id) {
        this.resolvedPartId = part.id;
        return part.id;
      }
    } catch (error) {
      console.error('Failed to resolve part number', error);
    }

    return undefined;
  }
}
